import { Loader2, Paperclip, X } from "lucide-react";
import {
  MAX_ATTACHMENT_BYTES,
  type AttachmentFailure,
  type PendingAttachment,
} from "@/features/intelligence/lib/attachments";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { cn } from "@/shared/cn";

export function AttachmentChips({
  attachments,
  onRemove,
}: {
  attachments: readonly PendingAttachment[];
  onRemove: (key: string) => void;
}) {
  if (attachments.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-start gap-[var(--space-2)] mb-[var(--space-2)] max-h-[76px] overflow-y-auto">
      {attachments.map((a) => (
        <li key={a.key}>
          <AttachmentChip attachment={a} onRemove={() => onRemove(a.key)} />
        </li>
      ))}
    </ul>
  );
}

function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: PendingAttachment;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const failed = attachment.status === "failed";
  const label =
    failed && attachment.failure
      ? t(FAILURE_KEY[attachment.failure], { name: attachment.name, limit: LIMIT_LABEL })
      : attachment.name;
  return (
    <div
      className={cn(
        "flex max-w-[280px] items-center gap-[var(--space-2)] rounded-[var(--radius-md)]",
        "border px-[var(--space-2)] py-[6px] font-(family-name:--font-display) text-xs",
        failed
          ? "border-[var(--color-withdrawal)] bg-[var(--color-light-03)] text-[var(--color-withdrawal)]"
          : "border-[rgba(251,251,245,0.5)] bg-[var(--color-light-03)] text-[var(--color-text-strong)]",
      )}
    >
      {attachment.status === "uploading" ? (
        <Loader2
          size={13}
          className="shrink-0 animate-spin"
          aria-label={t("intelligence.attachUploading")}
        />
      ) : (
        <Paperclip size={13} className="shrink-0" aria-hidden />
      )}
      <span className="min-w-0 flex-1 truncate" title={label}>
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("intelligence.attachRemove", { name: attachment.name })}
        title={t("intelligence.attachRemove", { name: attachment.name })}
        className={cn(
          "shrink-0 rounded-[var(--radius-sm)] p-[2px] transition-colors duration-[var(--duration-fast)]",
          "hover:bg-[var(--color-hover-faint)] focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
        )}
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}

const FAILURE_KEY: Record<AttachmentFailure, string> = {
  "too-large": "intelligence.attachFailedTooLarge",
  empty: "intelligence.attachFailedEmpty",
  timeout: "intelligence.attachFailedTimeout",
  rejected: "intelligence.attachFailedRejected",
};

const LIMIT_LABEL = `${Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024))} MB`;
